import { useCallback } from 'react';
import { Column, Table, VisibilityState } from '@tanstack/react-table';
import {
	UseSubColumnToggleOptions,
	UseSubColumnToggleReturn,
} from '@dealstack/frontend/app/pages/Valuations/components/table/hooks/useSubColumnToggle/useSubColumnToggle.types';
import {
	getColumnMeta,
	getColumnWithMeta,
} from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder.utils';

/**
 * Custom hook for managing subcolumn visibility in TanStack React Tables
 *
 * This hook provides utilities to toggle visibility of subcolumns based on a parent column's metadata.
 * The parent column should have a `subColumns` array in its meta property containing the IDs of child columns.
 *
 * @param options - Configuration options for the hook
 * @returns Object containing toggle functions
 *
 * @example
 * ```tsx
 * const { toggleSubColumns } = useSubColumnToggle();
 *
 * // In column definition:
 * const columns = [
 *   {
 *     id: 'proceeds',
 *     header: ({ column, table }) => (
 *       <div onClick={() => toggleSubColumns(column, table)}>
 *         Proceeds
 *       </div>
 *     ),
 *     meta: {
 *       subColumns: ['securitiesHeld', 'receivables', 'cashOnBalanceSheet']
 *     }
 *   }
 * ];
 * ```
 */
export function useSubColumnToggle<TData>(
	options: UseSubColumnToggleOptions = {}
): UseSubColumnToggleReturn<TData> {
	const { defaultToggleState } = options;

	const hasVisibleSubColumns = useCallback(
		(column: Column<TData, unknown>, table: Table<TData>) => {
			const meta = getColumnMeta(column);

			if (!meta?.subColumns || !Array.isArray(meta.subColumns)) {
				return false;
			}

			const currentVisibility = table.getState().columnVisibility;
			return meta.subColumns.some((subColumnId) => currentVisibility[subColumnId] !== false);
		},
		[]
	);

	const setSubColumnsVisibility = useCallback(
		(column: Column<TData, unknown>, table: Table<TData>, visible: boolean) => {
			const meta = getColumnMeta(column);

			if (!meta?.subColumns || !Array.isArray(meta.subColumns)) {
				return;
			}

			// Create visibility update object
			const subColumnVisibility = meta.subColumns.reduce((acc, subColumnId) => {
				acc[subColumnId] = visible;
				return acc;
			}, {} as VisibilityState);

			// Apply the visibility changes
			table.setColumnVisibility((prev) => ({
				...prev,
				...subColumnVisibility,
			}));
		},
		[]
	);

	const toggleSubColumns = useCallback(
		(column: Column<TData, unknown>, table: Table<TData>) => {
			const meta = getColumnMeta(column);

			if (!meta?.subColumns || !Array.isArray(meta.subColumns)) {
				return;
			}

			// Determine new visibility state
			const newVisibility =
				defaultToggleState !== undefined
					? defaultToggleState
					: !hasVisibleSubColumns(column, table);

			setSubColumnsVisibility(column, table, newVisibility);
		},
		[defaultToggleState, hasVisibleSubColumns, setSubColumnsVisibility]
	);

	const toggleSubColumnsByColumnId = useCallback(
		(columnId: string, table: Table<TData>) => {
			const column = table.getColumn(columnId);
			if (!column) {
				return;
			}
			toggleSubColumns(column, table);
		},
		[toggleSubColumns]
	);

	// Helper function to determine if column is last sub-column
	const isLastSubColumn = useCallback(
		(columnId: string, table: Table<TData>, meta: { isSubColumn?: boolean } | undefined) => {
			if (!meta?.isSubColumn) {
				return false;
			}
			const allColumns = table.getVisibleFlatColumns();
			const currentColumnIndex = allColumns.findIndex((col) => col.id === columnId);

			if (currentColumnIndex === -1) {
				return true;
			}

			const nextColumnEntry = allColumns[currentColumnIndex + 1];
			if (!nextColumnEntry) {
				return true;
			}

			const nextColumn = getColumnWithMeta(table, nextColumnEntry.id);

			if (!nextColumn) {
				return true;
			}

			const isLastSubColumn = !nextColumn.columnDef.meta.isSubColumn;
			return isLastSubColumn;
		},
		[]
	);

	return {
		toggleSubColumns,
		toggleSubColumnsByColumnId,
		setSubColumnsVisibility,
		hasVisibleSubColumns,
		isLastSubColumn,
	};
}
